import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Shimmer } from "../Shimmer/Shimmer";
import { ItCheckTable } from "../ItCheckTable/ItCheckTable";
import { CreateItCheckModal } from "../CreateItCheckModal/CreateItCheckModal";
import { TariffAccessBanner } from "../TariffAccessBanner/TariffAccessBanner";
import { useItCheckList } from "../../hooks/useItCheckList";
import { useTariffAccess } from "../../hooks/useTariffAccess";
import type { AdminTabId } from "../AdminWorkspace/adminTabs";
import type { UserTabId } from "../UserWorkspace/userTabs";
import "../RequestsSection/RequestsSection.scss";

interface Props {
  enabled?: boolean;
  showHeader?: boolean;
  embedded?: boolean;
  returnTab?: AdminTabId | UserTabId;
}

export const ItCheckSection = ({
  enabled = true,
  showHeader = true,
  embedded = false,
  returnTab,
}: Props) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const { data, loading, error, refetch } = useItCheckList({ page, pageSize, enabled });
  const access = useTariffAccess();
  const createOff = !access.canItCheck;

  if (!enabled) return null;

  return (
    <section
      className={`requests-section${embedded ? " requests-section--embedded" : ""}`}
      aria-label="Проверка минцифры"
    >
      {showHeader ? (
        <div className="requests-section-header">
          <h1 className="requests-section-title">Проверка минцифры</h1>
          <button
            type="button"
            className="requests-section-create-btn"
            disabled={createOff}
            onClick={() => setOpen(true)}
          >
            <Plus size={20} />
            <span>Создать заявку</span>
          </button>
        </div>
      ) : null}

      {access.itCheckBanner ? (
        <TariffAccessBanner
          banner={access.itCheckBanner}
          blockReason={access.itCheckBlockReason}
        />
      ) : null}

      {loading ? (
        <Shimmer rows={5} />
      ) : error ? (
        <div className="requests-section-error">
          <p className="requests-section-error__text">{error}</p>
          <button type="button" className="requests-section-error__retry" onClick={refetch}>
            Повторить
          </button>
        </div>
      ) : (
        <ItCheckTable
          data={data}
          page={page}
          pageSize={pageSize}
          returnTab={returnTab}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {open ? (
        <CreateItCheckModal
          onClose={() => setOpen(false)}
          onCreated={(result) => {
            setOpen(false);
            refetch();
            const path = result.openScheduleSetup
              ? `/it-check/${result.id}?openScheduleSetup=1`
              : `/it-check/${result.id}`;
            navigate(path, returnTab ? { state: { returnTab } } : undefined);
          }}
        />
      ) : null}
    </section>
  );
};
